import { WaterLocomotion, PlayerGait, PlayerMotionMode, PlayerStance, sampleWorldWaterAt, type PackedPlayer, type EquipmentVisualState, type PackedEquipment } from "@endclock/shared";
import { DEFAULT_EQUIPMENT_VISUAL, equipmentMeshGroups, heldFlashlightLight } from "./held-equipment.js";
import type { LocalLight } from "../engine/lighting.js";
import type { AtmosphereFeatureFlags } from "../engine/atmosphere.js";
import type { Camera, CameraCollisionProbe } from "../engine/camera.js";
import type { ActorContactShadows } from "../engine/actor-contact-shadows.js";
import { writeUprightTransform, type Mat4, type Vec3 } from "../engine/math.js";
import { createDebugMarker } from "../engine/mesh.js";
import type { GpuMesh, Renderer } from "../engine/renderer.js";
import type { HeightfieldTerrain } from "../terrain/terrain.js";
import type { ConnectionState, HydratedPlayer, NetworkSnapshot } from "../game/network.js";
import { CharacterAnimator, type CharacterPoseControl, boundedCharacterWetness, characterPixelHeight, facingAngle, smoothFacing } from "./character-animation.js";
import { loadCharacterGlb, type CharacterAsset, type CharacterClipName } from "./character-asset.js";
import { CharacterRenderer } from "./character-renderer.js";
import { RemotePlayerInterpolation, REMOTE_INTERPOLATION_DELAY_MS, REMOTE_MAX_EXTRAPOLATION_MS } from "../game/remote-player-interpolation.js";
import { queryGameplayAim } from "../game/aim-query.js";
import { RAGDOLL_MAX_ACTIVE_CHARACTERS } from "./articulated-ragdoll.js";

export type CharacterLoadState = "loading" | "ready" | "fallback";

const CHARACTER_URL = "/assets/characters/survivor_01.glb?v=locomotion-v2";

const CHARACTER_CULL_RADIUS = 1.5;

interface CharacterInstance {
  readonly id: string;
  readonly animator: CharacterAnimator;
  readonly model: Mat4;
  equipment: EquipmentVisualState;
  meshGroups: Set<string>;
  x: number;
  y: number;
  z: number;
  yaw: number;
  targetYaw: number;
  speed: number;
  running: boolean;
  locomotion: WaterLocomotion;
  wetness: number;
  control: CharacterPoseControl | undefined;
}

export interface CharacterSystemSnapshot {
  readonly state: CharacterLoadState;
  readonly error?: string;
  readonly representation: "3D";
  readonly assetId: string;
  readonly standingHeight: number;
  readonly groundOriginY: number;
  readonly triangleCount: number;
  readonly jointCount: number;
  readonly clips: readonly CharacterClipName[];
  readonly sourceBytes: number;
  readonly meshBufferBytes: number;
  readonly animationBytes: number;
  readonly runtimeBytesPerCharacter: number;
  readonly localAnimation: CharacterClipName;
  readonly localSpeed: number;
  readonly localYaw: number;
  readonly localGroundY: number;
  readonly localWetness: number;
  readonly localEquipment: EquipmentVisualState;
  readonly localMeshGroups: readonly string[];
  readonly heldLights: readonly LocalLight[];
  readonly localLocomotion: WaterLocomotion;
  readonly localPose: CharacterPoseControl | undefined;
  readonly localRagdoll: { readonly age: number; readonly steps: number; readonly droppedSeconds: number;
    readonly pelvisHeight: number; readonly pelvisPitch: number; readonly pelvisTilt: number; readonly leftKnee: number; readonly rightKnee: number;
    readonly leftElbow: number; readonly rightElbow: number; readonly settled: boolean } | undefined;
  readonly localAnimationCpuMs: number;
  readonly remoteAnimationCpuMsPerPlayer: number;
  readonly ragdollCpuMs: number;
  readonly ragdollActiveCharacters: number;
  readonly ragdollActiveBodies: number;
  readonly remoteStates: readonly (CharacterPoseControl & { id: string; x: number; y: number; z: number; interpolationDepth: number; snapshotAge: number })[];
  readonly remoteCount: number;
  readonly remoteMovingCount: number;
  readonly remoteIds: readonly string[];
  readonly remoteAnimations: readonly CharacterClipName[];
  readonly remoteWetness: readonly number[];
  readonly benchmarkCount: number;
  readonly visibleCount: number;
  readonly fallbackVisible: boolean;
  readonly markerVisible: boolean;
  readonly pixelHeight: { readonly closest: number; readonly default: number; readonly farthest: number };
}

/** Presentation adapter over the protocol's timestamped snapshot buffer. */
export class RemoteCharacterInterpolator {
  x: number; y: number; z: number; yaw: number;
  speed = 0; running = false; wetness = 0;
  locomotion = WaterLocomotion.Ground;
  control: CharacterPoseControl;
  readonly buffer = new RemotePlayerInterpolation();
  snapshotAge = 0;
  private serverNow = 0;
  private clockNeedsSeed = false;
  private latestServerAt = 0;
  private latestTick = 0;
  constructor(x: number, y: number, z: number, yaw: number) {
    this.x = x; this.y = y; this.z = z; this.yaw = yaw;
    this.control = { bodyYaw: yaw, aimYaw: yaw, aimPitch: 0, stance: PlayerStance.Standing, gait: PlayerGait.Walk, motionMode: PlayerMotionMode.Active };
  }
  ingest(x: number, y: number, z: number, yaw: number, serverAt: number, tick: number,
    locomotion = WaterLocomotion.Ground, wetness = 0, velocityX = 0, velocityZ = 0,
    control: CharacterPoseControl = this.control, generation = 0): void {
    const player: PackedPlayer = ["remote", x, y, z, yaw, 0, locomotion, wetness, velocityX, control.velocityY ?? 0, velocityZ,
      control.aimYaw, control.aimPitch, control.stance, control.gait, control.motionMode, control.ragdollEvent ?? 0,
      control.motionStartTick ?? control.ragdollStartTick ?? 0, control.impulseX ?? 0, control.impulseZ ?? 0,
      control.getUpVariant ?? 0, control.motionEpoch ?? 0];
    if (!this.buffer.push(player, serverAt, tick, generation)) return;
    this.latestServerAt = serverAt; this.latestTick = tick; this.snapshotAge = 0;
    if (this.buffer.size === 1) {
      this.serverNow = serverAt; this.update(0, 0);
      this.clockNeedsSeed = true;
    }
  }
  update(delta: number, snapshotAge?: number): void {
    const elapsed = Number.isFinite(delta) ? Math.max(0, delta) : 0;
    this.snapshotAge = snapshotAge !== undefined && Number.isFinite(snapshotAge) ? Math.max(0, snapshotAge) : this.snapshotAge + elapsed;
    const targetNow = this.latestServerAt + this.snapshotAge * 1000;
    // Seed a new/teleported timeline from the fresh sample, avoiding charging
    // the frame gap before that sample existed. Thereafter the clock advances
    // on elapsed render time: receive age resets with each packet and cannot
    // measure the time between frames, especially below the snapshot rate.
    const predictedNow = this.clockNeedsSeed ? targetNow : this.serverNow + elapsed * 1000;
    this.clockNeedsSeed = false;
    const correctionLimit = Math.max(2, elapsed * 1000 * 0.12);
    const correction = Math.max(-correctionLimit, Math.min(correctionLimit, targetNow - predictedNow));
    this.serverNow = Math.max(this.serverNow, predictedNow + correction);
    const player = this.buffer.sample(this.serverNow); if (!player) return;
    this.x = player[1]; this.y = player[2]; this.z = player[3]; this.yaw = player[4];
    this.speed = this.snapshotAge * 1000 > REMOTE_INTERPOLATION_DELAY_MS + REMOTE_MAX_EXTRAPOLATION_MS ? 0 : Math.hypot(player[8], player[10]);
    this.running = player[14] !== PlayerGait.Walk && this.speed > 0.1;
    this.locomotion = player[6]; this.wetness = boundedCharacterWetness(player[7]);
    this.control = { ...poseControlFromPacked(player), velocityX: this.speed ? player[8] : 0, velocityZ: this.speed ? player[10] : 0,
      stateAgeSeconds: Math.max(0, (this.latestTick - player[17]) * 0.05 + (this.serverNow - this.latestServerAt - REMOTE_INTERPOLATION_DELAY_MS) / 1000) };
  }
}

interface RemoteCharacter {
  readonly motion: RemoteCharacterInterpolator;
  readonly instance: CharacterInstance;
}

export class CharacterWorld {
  state: CharacterLoadState = "loading";
  loadError: string | undefined;
  private asset: CharacterAsset | undefined;
  private localEquipment = DEFAULT_EQUIPMENT_VISUAL;
  private readonly remoteEquipment = new Map<string, EquipmentVisualState>();
  private characterRenderer: CharacterRenderer | undefined;
  private local: CharacterInstance | undefined;
  private fallbackMesh: GpuMesh | undefined;
  private readonly fallbackModel = new Float32Array(16);
  private readonly remoteNames = new Map<string, string>();
  private readonly remotes = new Map<string, RemoteCharacter>();
  private readonly remoteList: RemoteCharacter[] = [];
  private readonly presentRemoteIds = new Set<string>();
  private readonly benchmarkCharacters: CharacterInstance[] = [];
  private latestNetworkGeneration = -1;
  private latestNetworkTick = -1;
  private localX = 0;
  private localZ = 12;
  private localGroundY = 0;
  private localYaw = Math.PI;
  private localSpeed = 0;
  private localRunning = false;
  private localLocomotion: WaterLocomotion = WaterLocomotion.Ground;
  private localWetness = 0;
  private delta = 1 / 60;
  private smoothedAnimationCpuMs = 0;
  private frameAnimationCpuMs = 0;
  private visibleCount = 0;
  private fallbackVisible = false;
  private updateRevision = 0;
  private preparedRevision = -1;
  private readonly preparedInstances: CharacterInstance[] = [];
  private localAimPoint: Vec3 | undefined;
  private aimProbe: CameraCollisionProbe | undefined;
  private localAnimationCpuMs = 0;
  private remoteAnimationCpuMs = 0;
  private ragdollCpuMs = 0;
  private ragdollActiveCharacters = 0;

  setLocalAimPoint(point: Vec3 | undefined, probe?: CameraCollisionProbe): void {
    this.localAimPoint = point; if (probe) this.aimProbe = probe;
  }

  setLocalEquipmentVisual(state: EquipmentVisualState): void {
    this.localEquipment = state;
    if (this.local) this.applyEquipment(this.local, state);
  }

  setRemoteEquipmentVisuals(entries: readonly PackedEquipment[]): void {
    this.remoteEquipment.clear();
    for (const [id, state] of entries) this.remoteEquipment.set(id, state);
    for (const remote of this.remoteList) this.applyEquipment(remote.instance, this.remoteEquipment.get(remote.instance.id) ?? DEFAULT_EQUIPMENT_VISUAL);
  }

  /** Call after prepare() so emitter and mesh use this frame's identical pose. */
  collectHeldLights(): readonly LocalLight[] {
    if (!this.asset) return [];
    const lights: LocalLight[] = [];
    for (const instance of this.preparedInstances) {
      if (instance.locomotion === WaterLocomotion.Swimming) continue;
      let light = heldFlashlightLight(instance.id, instance.equipment, this.asset, instance.animator.palette, instance.model);
      if (light) {
        // The lens can meet a nearby wall before the camera ray does. Keep the
        // source attached, suppress embedded lights, and bound its throw at the
        // first proxy hit without allocating additional shadow resources.
        if (this.aimProbe && light.direction) {
          const obstruction = queryGameplayAim([...light.position], [...light.direction], this.aimProbe, Math.min(2, light.radius));
          if (obstruction.hit && obstruction.distance < 0.06) continue;
          if (obstruction.hit) light = { ...light, radius: Math.min(light.radius, obstruction.distance + 0.05) };
        }
        lights.push(light);
      }
    }
    return lights;
  }

  private applyEquipment(instance: CharacterInstance, state: EquipmentVisualState): void {
    instance.equipment = state;
    instance.meshGroups = equipmentMeshGroups(state);
    instance.animator.heldItemPose = !!state.mainHand;
  }

  get facingYaw(): number {
    return this.localYaw;
  }

  constructor(
    private readonly renderer: Renderer,
    private readonly terrain: HeightfieldTerrain,
    atmosphereFlags: AtmosphereFeatureFlags,
  ) {
    void loadCharacterGlb(CHARACTER_URL)
      .then((asset) => {
        this.asset = asset;
        this.characterRenderer = new CharacterRenderer(renderer, asset, atmosphereFlags);
        this.local = createInstance("local", asset, this.localX, this.localGroundY, this.localZ, this.localYaw);
        this.applyEquipment(this.local, this.localEquipment);
        this.state = "ready";
      })
      .catch((error: unknown) => {
        this.loadError = error instanceof Error ? error.message : "Unknown character asset error";
        this.fallbackMesh = renderer.createMesh(createDebugMarker());
        this.state = "fallback";
      });
  }

  updateLocal(
    x: number,
    y: number,
    z: number,
    actualVelocityX: number,
    actualVelocityZ: number,
    running: boolean,
    delta: number,
    movementHeading?: number,
    locomotion: WaterLocomotion = WaterLocomotion.Ground,
    wetness = 0,
    visualElapsed = delta,
    snapshotAge?: number,
    control?: CharacterPoseControl,
  ): void {
    // Pose clocks use elapsed render time. The articulated solver applies its
    // own fixed-step/catch-up cap; feeding the 50 ms movement clamp here would
    // slow the fall twice on a low-FPS client before server recovery arrives.
    this.delta = Number.isFinite(visualElapsed) ? Math.max(0, visualElapsed) : 0;
    this.updateRevision += 1;
    this.localX = x;
    this.localZ = z;
    this.localGroundY = y;
    this.localSpeed = Math.hypot(actualVelocityX, actualVelocityZ);
    this.localRunning = running;
    this.localLocomotion = locomotion;
    this.localWetness = boundedCharacterWetness(wetness);
    // TPS facing follows committed intent, including reversals before velocity
    // catches up. Actual speed still drives the existing WALK/RUN animations.
    const nextFacing = control?.bodyYaw ?? (locomotion === WaterLocomotion.Swimming
      ? facingAngle(actualVelocityX, actualVelocityZ, this.localYaw)
      : movementHeading ?? facingAngle(actualVelocityX, actualVelocityZ, this.localYaw));
    this.localYaw = control ? smoothFacing(this.localYaw, nextFacing, visualElapsed, 24) : smoothFacing(this.localYaw, nextFacing, delta);
    if (this.local) {
      this.local.x = x;
      this.local.y = y;
      this.local.z = z;
      this.local.speed = this.localSpeed;
      this.local.running = running;
      this.local.locomotion = locomotion;
      this.local.wetness = this.localWetness;
      this.local.targetYaw = nextFacing;
      this.local.yaw = this.localYaw;
      this.local.control = control ? { ...control, bodyYaw: this.localYaw, velocityX: actualVelocityX, velocityZ: actualVelocityZ } : undefined;
    }
    for (let index = 0; index < this.remoteList.length; index += 1) {
      const remote = this.remoteList[index]!;
      remote.motion.update(visualElapsed, snapshotAge);
      remote.instance.x = remote.motion.x;
      remote.instance.y = remote.motion.y;
      remote.instance.z = remote.motion.z;
      remote.instance.yaw = remote.motion.yaw;
      remote.instance.targetYaw = remote.motion.yaw;
      remote.instance.speed = remote.motion.speed;
      remote.instance.running = remote.motion.running;
      remote.instance.locomotion = remote.motion.locomotion;
      remote.instance.wetness = remote.motion.wetness;
      remote.instance.control = remote.motion.control;
    }
    this.updateBenchmarkPositions();
  }

  ingestNetwork(snapshot: NetworkSnapshot, connectionState: ConnectionState): void {
    if (this.state !== "ready" || !this.asset) return;
    if (connectionState !== "online") {
      this.resetNetworkState(-1);
      return;
    }
    if (snapshot.generation !== this.latestNetworkGeneration) this.resetNetworkState(snapshot.generation);
    if (snapshot.tick <= this.latestNetworkTick) return;
    this.latestNetworkTick = snapshot.tick;
    const present = this.presentRemoteIds;
    present.clear();
    for (const player of snapshot.others) {
      present.add(player[0]);
      this.ingestRemote(player, snapshot.serverAt, snapshot.tick);
    }
    for (let index = this.remoteList.length - 1; index >= 0; index -= 1) {
      const remote = this.remoteList[index]!;
      if (present.has(remote.instance.id)) continue;
      this.remotes.delete(remote.instance.id);
      this.remoteNames.delete(remote.instance.id);
      this.remoteList.splice(index, 1);
    }
  }

  render(camera: Camera): void {
    this.fallbackVisible = false;
    this.visibleCount = 0;
    this.renderer.metrics.totalCharacters = 1 + this.remotes.size + this.benchmarkCharacters.length;
    if (this.state === "fallback" && this.fallbackMesh) {
      writeUprightTransform(this.fallbackModel, 0, this.localX, this.localGroundY + 0.025, this.localZ, this.localYaw, 1);
      this.renderer.drawMarker(this.fallbackMesh, this.fallbackModel);
      this.fallbackVisible = true;
      this.visibleCount = 1;
      this.renderer.metrics.visibleCharacters = 1;
      return;
    }
    const characterRenderer = this.characterRenderer;
    const local = this.local;
    const asset = this.asset;
    if (!characterRenderer || !local || !asset) return;

    this.prepare(camera);
    characterRenderer.begin(camera.viewProjection, camera.position, camera.target);
    this.drawInstance(characterRenderer, local, true, camera);
    for (let index = 0; index < this.remoteList.length; index += 1) {
      this.drawInstance(characterRenderer, this.remoteList[index]!.instance, false, camera);
    }
    for (let index = 0; index < this.benchmarkCharacters.length; index += 1) {
      this.drawInstance(characterRenderer, this.benchmarkCharacters[index]!, false, camera);
    }
    this.renderer.metrics.characterAnimationCpuMs = this.smoothedAnimationCpuMs;
    this.renderer.metrics.visibleCharacters = this.visibleCount;
    this.renderer.metrics.characterJoints = asset.joints.length;
    this.renderer.metrics.characterJointUpdates = this.preparedInstances.length * asset.joints.length;
  }

  /** Prepare the camera/shadow visibility union once, before either draw pass. */
  prepare(camera: Camera, shadowCamera?: Camera): void {
    if (this.preparedRevision === this.updateRevision || !this.local || !this.characterRenderer) return;
    this.preparedRevision = this.updateRevision;
    this.preparedInstances.length = 0;
    this.frameAnimationCpuMs = 0;
    this.remoteAnimationCpuMs = 0; this.ragdollCpuMs = 0; this.ragdollActiveCharacters = 0;
    this.prepareInstance(this.local, true, camera, shadowCamera);
    for (const remote of this.remoteList) this.prepareInstance(remote.instance, false, camera, shadowCamera);
    for (const instance of this.benchmarkCharacters) this.prepareInstance(instance, false, camera, shadowCamera);
    this.smoothedAnimationCpuMs += (this.frameAnimationCpuMs - this.smoothedAnimationCpuMs) * 0.12;
  }

  renderShadow(shadowCamera: Camera): void {
    const renderer = this.characterRenderer;
    if (!renderer) return;
    renderer.beginShadow(shadowCamera.viewProjection);
    for (const instance of this.preparedInstances) {
      if (shadowCamera.isSphereVisible(instance.x, instance.y + 0.89, instance.z, CHARACTER_CULL_RADIUS)) {
        renderer.drawShadow(instance.model, instance.animator.palette, instance.meshGroups);
      }
    }
  }

  addContactShadows(shadows: ActorContactShadows): void {
    if (this.state !== "ready") return;
    if (this.localLocomotion !== WaterLocomotion.Swimming) shadows.addActor(this.localX, this.localGroundY, this.localZ);
    for (const remote of this.remoteList) {
      if (remote.instance.locomotion !== WaterLocomotion.Swimming) shadows.addActor(remote.instance.x, remote.instance.y, remote.instance.z);
    }
    for (const instance of this.benchmarkCharacters) shadows.addActor(instance.x, instance.y, instance.z);
  }

  setBenchmarkCharacterCount(count: number): void {
    const target = Math.max(0, Math.min(25, Math.floor(count)));
    const asset = this.asset;
    if (!asset) return;
    while (this.benchmarkCharacters.length < target) {
      const index = this.benchmarkCharacters.length;
      const character = createInstance(
        `benchmark_${index}`,
        asset,
        this.localX,
        this.localGroundY,
        this.localZ,
        index * 0.73,
      );
      character.speed = index % 3 === 0 ? 6.2 : 4.2;
      character.running = index % 3 === 0;
      this.benchmarkCharacters.push(character);
    }
    this.benchmarkCharacters.length = target;
    this.updateBenchmarkPositions();
  }

  snapshot(): CharacterSystemSnapshot {
    const asset = this.asset;
    const local = this.local;
    return {
      state: this.state,
      ...(this.loadError ? { error: this.loadError } : {}),
      representation: "3D",
      assetId: asset?.id ?? "survivor_01",
      standingHeight: asset?.standingHeight ?? 1.78,
      groundOriginY: asset?.groundOriginY ?? 0,
      triangleCount: asset ? asset.geometry.indices.length / 3 : 0,
      jointCount: asset?.joints.length ?? 0,
      clips: asset ? Object.keys(asset.clips) as CharacterClipName[] : [],
      sourceBytes: asset?.sourceBytes ?? 0,
      meshBufferBytes: asset?.meshBufferBytes ?? 0,
      animationBytes: asset?.animationBytes ?? 0,
      runtimeBytesPerCharacter: local ? local.animator.runtimeBytes + local.model.byteLength : 0,
      localAnimation: local?.animator.state ?? "IDLE",
      localSpeed: this.localSpeed,
      localYaw: this.localYaw,
      localGroundY: this.localGroundY,
      localWetness: this.localWetness,
      localEquipment: this.localEquipment,
      localMeshGroups: [...(local?.meshGroups ?? [])],
      heldLights: this.collectHeldLights(),
      localLocomotion: this.localLocomotion,
      localPose: local?.control,
      localRagdoll: local?.animator.ragdoll ? {
        age: local.animator.ragdoll.age, steps: local.animator.ragdoll.steps,
        droppedSeconds: local.animator.ragdoll.droppedSeconds, pelvisHeight: local.animator.ragdoll.translations[4]!,
        pelvisPitch: local.animator.ragdoll.angles[3]!, pelvisTilt: local.animator.ragdoll.pelvisTilt,
        leftKnee: local.animator.ragdoll.angles[15]!, rightKnee: local.animator.ragdoll.angles[21]!,
        leftElbow: local.animator.ragdoll.angles[27]!, rightElbow: local.animator.ragdoll.angles[33]!, settled: local.animator.ragdoll.settled,
      } : undefined,
      localAnimationCpuMs: this.localAnimationCpuMs,
      remoteAnimationCpuMsPerPlayer: this.remoteList.length ? this.remoteAnimationCpuMs / this.remoteList.length : 0,
      ragdollCpuMs: this.ragdollCpuMs,
      ragdollActiveCharacters: this.ragdollActiveCharacters,
      ragdollActiveBodies: this.ragdollActiveCharacters * Math.max(0, (asset?.joints.length ?? 1) - 1),
      remoteStates: this.remoteList.map(({ instance, motion }) => ({ ...motion.control, id: instance.id,
        x: instance.x, y: instance.y, z: instance.z, interpolationDepth: motion.buffer.size, snapshotAge: motion.snapshotAge })),
      remoteCount: this.remotes.size,
      remoteMovingCount: [...this.remotes.values()].filter((remote) => remote.instance.speed >= 0.1).length,
      remoteIds: [...this.remotes.keys()],
      remoteAnimations: [...this.remotes.values()].map((remote) => remote.instance.animator.state),
      remoteWetness: [...this.remotes.values()].map((remote) => remote.instance.wetness),
      benchmarkCount: this.benchmarkCharacters.length,
      visibleCount: this.visibleCount,
      fallbackVisible: this.fallbackVisible,
      markerVisible: this.fallbackVisible,
      pixelHeight: {
        closest: characterPixelHeight(18),
        default: characterPixelHeight(24),
        farthest: characterPixelHeight(64),
      },
    };
  }

  nameplatePlayers(): readonly { id: string; name: string; x: number; y: number; z: number }[] {
    return this.remoteList.map(({ instance }) => ({
      id: instance.id, name: this.remoteNames.get(instance.id) ?? "Guest", x: instance.x, y: instance.y, z: instance.z,
    }));
  }

  /** Emit at the interpolated model position so remote wakes do not lead it. */
  updateRemoteWaterEffects(time: number, viewerX: number, viewerZ: number): void {
    for (const { instance } of this.remoteList) {
      if (Math.hypot(instance.x - viewerX, instance.z - viewerZ) > 32) continue;
      const water = sampleWorldWaterAt(instance.x, instance.z);
      this.renderer.updateWaterActor(instance.id, instance.x, instance.z, water.surfaceY,
        Math.sin(instance.yaw) * instance.speed, Math.cos(instance.yaw) * instance.speed, instance.locomotion, time);
    }
  }

  private ingestRemote(player: HydratedPlayer, serverAt: number, tick: number): void {
    const id = player[0];
    this.remoteNames.set(id, player[22].name);
    let remote = this.remotes.get(id);
    if (!remote) {
      const motion = new RemoteCharacterInterpolator(player[1], player[2], player[3], player[4]);
      remote = { motion, instance: createInstance(id, this.asset!, player[1], player[2], player[3], player[4]) };
      this.applyEquipment(remote.instance, this.remoteEquipment.get(id) ?? DEFAULT_EQUIPMENT_VISUAL);
      this.remotes.set(id, remote);
      this.remoteList.push(remote);
    }
    // Phase 8 intentionally maps every retained DEFAULT_APPEARANCE/custom payload to survivor_01.
    remote.motion.ingest(player[1], player[2], player[3], player[4], serverAt, tick, player[6], player[7], player[8], player[10], poseControlFromPacked(player), this.latestNetworkGeneration);
  }

  private resetNetworkState(generation: number): void {
    this.remotes.clear();
    this.remoteNames.clear();
    this.remoteList.length = 0;
    this.latestNetworkGeneration = generation;
    this.latestNetworkTick = -1;
  }

  private drawInstance(
    characterRenderer: CharacterRenderer,
    instance: CharacterInstance,
    local: boolean,
    camera: Camera,
  ): void {
    const ground = instance.y;
    if (!local && !camera.isSphereVisible(instance.x, ground + 0.89, instance.z, CHARACTER_CULL_RADIUS)) return;
    characterRenderer.draw(instance.model, instance.animator.palette, instance.wetness, instance.meshGroups);
    this.visibleCount += 1;
  }

  private prepareInstance(instance: CharacterInstance, local: boolean, camera: Camera, shadowCamera?: Camera): void {
    if (!local && !(instance.equipment.mainHand?.lightOn && Math.hypot(instance.x - this.localX, instance.z - this.localZ) < 48)
      && !camera.isSphereVisible(instance.x, instance.y + 0.89, instance.z, CHARACTER_CULL_RADIUS)
      && !shadowCamera?.isSphereVisible(instance.x, instance.y + 0.89, instance.z, CHARACTER_CULL_RADIUS)
      && !this.renderer.isLocalShadowCaster(instance.x, instance.y + 0.89, instance.z, CHARACTER_CULL_RADIUS)) return;
    const animationStartedAt = performance.now();
    const ragdoll = instance.control?.motionMode === PlayerMotionMode.Ragdoll;
    const physics = ragdoll || instance.control?.motionMode === PlayerMotionMode.GettingUp && instance.animator.hasActiveRagdoll;
    const allowRagdoll = this.ragdollActiveCharacters < RAGDOLL_MAX_ACTIVE_CHARACTERS;
    if (physics && allowRagdoll) this.ragdollActiveCharacters += 1;
    const cosine = Math.cos(instance.yaw), sine = Math.sin(instance.yaw);
    const ground = { heightAt: (x: number, z: number) => this.terrain.getHeightAt(instance.x + x * cosine + z * sine,
      instance.z - x * sine + z * cosine) - instance.y };
    const animationControl = instance.control && ragdoll ? { ...instance.control,
      impulseX: (instance.control.impulseX ?? 0) * cosine - (instance.control.impulseZ ?? 0) * sine,
      impulseZ: (instance.control.impulseX ?? 0) * sine + (instance.control.impulseZ ?? 0) * cosine } : instance.control;
    instance.animator.update(instance.speed, instance.running, this.delta, instance.locomotion, animationControl, ground, allowRagdoll);
    writeUprightTransform(instance.model, 0, instance.x, instance.y, instance.z, instance.yaw, 1);
    if (instance.control && instance.locomotion !== WaterLocomotion.Swimming && instance.control.motionMode === PlayerMotionMode.Active) {
      const control = instance.control;
      const target = local && this.localAimPoint ? this.localAimPoint : [instance.x + Math.sin(control.aimYaw) * Math.cos(control.aimPitch) * 60,
        instance.y + 1.3 + Math.sin(control.aimPitch) * 60, instance.z + Math.cos(control.aimYaw) * Math.cos(control.aimPitch) * 60];
      const dx = target[0]! - instance.x, dz = target[2]! - instance.z;
      instance.animator.aimHeldEmitterAt([dx * cosine - dz * sine, target[1]! - instance.y, dx * sine + dz * cosine]);
    }
    const cpu = performance.now() - animationStartedAt;
    this.frameAnimationCpuMs += cpu;
    if (local) this.localAnimationCpuMs = cpu; else this.remoteAnimationCpuMs += cpu;
    if (physics) this.ragdollCpuMs += cpu;
    this.preparedInstances.push(instance);
  }

  private updateBenchmarkPositions(): void {
    const count = this.benchmarkCharacters.length;
    for (let index = 0; index < count; index += 1) {
      const ring = 2.2 + Math.floor(index / 8) * 1.6;
      const angle = (index / Math.max(1, Math.min(8, count))) * Math.PI * 2 + Math.floor(index / 8) * 0.37;
      const character = this.benchmarkCharacters[index]!;
      character.x = this.localX + Math.sin(angle) * ring;
      character.y = this.terrain.getHeightAt(character.x, character.z);
      character.z = this.localZ + Math.cos(angle) * ring;
      character.yaw = angle + Math.PI;
      character.targetYaw = character.yaw;
    }
  }
}

function createInstance(id: string, asset: CharacterAsset, x: number, y: number, z: number, yaw: number): CharacterInstance {
  return {
    id,
    animator: new CharacterAnimator(asset),
    model: new Float32Array(16),
    equipment: DEFAULT_EQUIPMENT_VISUAL,
    meshGroups: equipmentMeshGroups(DEFAULT_EQUIPMENT_VISUAL),
    x,
    y,
    z,
    yaw,
    targetYaw: yaw,
    speed: 0,
    running: false,
    locomotion: WaterLocomotion.Ground,
    wetness: 0,
    control: undefined,
  };
}

function poseControlFromPacked(player: readonly [...PackedPlayer, ...unknown[]]): CharacterPoseControl {
  return { bodyYaw: player[4], aimYaw: player[11], aimPitch: player[12], stance: player[13], gait: player[14],
    motionMode: player[15], ragdollEvent: player[16], motionStartTick: player[17], impulseX: player[18], impulseZ: player[19],
    getUpVariant: player[20], motionEpoch: player[21], velocityY: player[9] };
}
